import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft } from "react-feather";
import toast from "react-hot-toast";
import {
  useGetVehicleQuery,
  useCreateVehicleMutation,
  useUpdateVehicleMutation,
} from "../vehiclesApi";
import { useGetBranchesQuery } from "../../../admin/adminApi";
import { useGetHrDesignationsQuery } from "../../designations/hrDesignationsApi";
import { useLazySearchEmployeesQuery } from "../../../users/employeesApi";
import { useClaims } from "../../../auth/useClaims";
import { useCanCreateVehicles, useCanEditVehicles } from "../useVehiclePermissions";
import { paths } from "../../../../routes/paths";

const emptyForm = {
  branchId: "",
  regNo: "",
  brand: "",
  model: "",
  makeYear: "",
  lastServiceMileage: "",
  nextServiceDueMileage: "",
  isActive: true,
};

// Toggle-switch styling, same pattern already used on EmployeeForm.jsx.
const ToggleSwitch = ({ id, checked, onChange, disabled, label }) => (
  <div className="form-check form-switch">
    <input
      type="checkbox"
      className="form-check-input"
      id={id}
      style={{ width: "2.5em", height: "1.4em" }}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
    <label className="form-check-label ms-2" htmlFor={id}>{label}</label>
  </div>
);

// Vehicle Master (handoff §20.1) - fleet roster, mirrors the Employee
// master's architecture: own Add/Edit route pages, vehicleId is globally
// unique (unlike custId/productCode) so no {branchId} composite key is
// needed here. Only the master-creation/edit screen is built this round -
// Maintenance Requests (§20.3) is a separate, not-yet-built screen.
//
// Allocation must be set to exactly one of an Employee or an HR Designation
// (verified live - both set or both null are rejected with a clear
// message), so the form uses a two-way toggle rather than two independent
// fields - picking one clears the other.
const VehicleForm = () => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(vehicleId);
  const canCreate = useCanCreateVehicles();
  const canEdit = useCanEditVehicles();
  const { branches: callerBranchCodes, isAllBranches } = useClaims();
  const { data: branches = [] } = useGetBranchesQuery();
  const { data: designations = [] } = useGetHrDesignationsQuery();
  const {
    data: detail,
    isLoading: isLoadingDetail,
    isError: isDetailError,
    error: detailError,
  } = useGetVehicleQuery(vehicleId, { skip: !isEditing });
  const [createVehicle, { isLoading: isCreating }] = useCreateVehicleMutation();
  const [updateVehicle, { isLoading: isUpdating }] = useUpdateVehicleMutation();
  const [triggerEmployeeSearch] = useLazySearchEmployeesQuery();

  const availableBranches = isAllBranches
    ? branches
    : branches.filter((b) => callerBranchCodes?.includes(b.branchCode));

  const [form, setForm] = useState(emptyForm);
  // "employee" | "designation" - which half of the mutually-exclusive
  // allocation is currently active on the form.
  const [allocationMode, setAllocationMode] = useState("designation");
  // Employee allocation is a code-lookup, not a name picker - per direct
  // instruction, keeping it to a plain "type the code, find the name" text
  // box (the /employees/search endpoint already supports an exact-code
  // lookup, same one EmployeePicker.jsx uses internally) rather than the
  // fuller AsyncSelect name-search picker used on the Users screen. Only
  // Designation gets a dropdown.
  const [empCodeInput, setEmpCodeInput] = useState("");
  const [resolvedEmployee, setResolvedEmployee] = useState(null);
  const [isSearchingEmpCode, setIsSearchingEmpCode] = useState(false);
  const [designationCode, setDesignationCode] = useState("");

  const handleFindEmployee = async () => {
    const code = empCodeInput.trim();
    if (!code) return;
    setIsSearchingEmpCode(true);
    try {
      const { data = [] } = await triggerEmployeeSearch(code);
      const match = data.find((emp) => String(emp.empCode) === code) || data[0];
      if (match) {
        setResolvedEmployee(match);
        setEmpCodeInput("");
      } else {
        toast.error(`No employee found for code ${code}`);
      }
    } finally {
      setIsSearchingEmpCode(false);
    }
  };

  useEffect(() => {
    if (isEditing && detail) {
      const v = detail;
      setForm({
        ...emptyForm,
        ...v,
        isActive: Boolean(v.isActive),
      });
      if (v.allocatedToEmployeeRecordGuid) {
        setAllocationMode("employee");
        setResolvedEmployee({
          recordGuid: v.allocatedToEmployeeRecordGuid,
          empName: v.allocatedToEmployeeName || v.allocatedToEmployeeRecordGuid,
          empLastName: "",
          empCode: "",
        });
        setDesignationCode("");
      } else {
        setAllocationMode("designation");
        setDesignationCode(v.allocatedToDesignationCode != null ? String(v.allocatedToDesignationCode) : "");
        setResolvedEmployee(null);
      }
      setEmpCodeInput("");
    } else if (!isEditing) {
      setForm({
        ...emptyForm,
        branchId: availableBranches.length === 1 ? availableBranches[0].branchId : "",
      });
      setAllocationMode("designation");
      setResolvedEmployee(null);
      setEmpCodeInput("");
      setDesignationCode("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, detail]);

  const setField = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const readOnly = isEditing ? !canEdit : !canCreate;

  const handleSave = async () => {
    if (!form.branchId) {
      toast.error("Branch is required");
      return;
    }
    if (!form.regNo.trim()) {
      toast.error("Registration number is required");
      return;
    }
    if (allocationMode === "employee" && !resolvedEmployee) {
      toast.error("Find an employee by code to allocate this vehicle to");
      return;
    }
    if (allocationMode === "designation" && !designationCode) {
      toast.error("Select a designation to allocate this vehicle to");
      return;
    }

    const body = {
      branchId: Number(form.branchId),
      regNo: form.regNo.trim(),
      brand: form.brand.trim() || null,
      model: form.model.trim() || null,
      makeYear: form.makeYear === "" ? null : Number(form.makeYear),
      allocatedToEmployeeRecordGuid: allocationMode === "employee" ? resolvedEmployee.recordGuid : null,
      allocatedToDesignationCode: allocationMode === "designation" ? Number(designationCode) : null,
      lastServiceMileage: form.lastServiceMileage === "" ? null : Number(form.lastServiceMileage),
      nextServiceDueMileage: form.nextServiceDueMileage === "" ? null : Number(form.nextServiceDueMileage),
      // Every update must resend isActive explicitly - verified live that
      // PUT silently defaults it to false if the field is omitted, it is
      // never preserved from the existing record automatically.
      isActive: form.isActive,
    };

    try {
      if (isEditing) {
        await updateVehicle({ vehicleId, ...body }).unwrap();
        toast.success("Vehicle saved");
        navigate(paths.vehicleEdit(vehicleId), { replace: true });
      } else {
        const created = await createVehicle(body).unwrap();
        toast.success("Vehicle saved");
        navigate(paths.vehicleEdit(created.vehicleId), { replace: true });
      }
    } catch (error) {
      toast.error(error?.message || "Could not save vehicle");
    }
  };

  if (isEditing && isDetailError) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link to={paths.vehicles} className="d-inline-flex align-items-center mb-3">
            <ArrowLeft size={16} className="me-1" /> Back to Vehicles
          </Link>
          <div className="alert alert-danger">
            Could not load this vehicle ({detailError?.message || "server error"}).
          </div>
        </div>
      </div>
    );
  }

  if (isEditing && isLoadingDetail) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.vehicles} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to Vehicles
              </Link>
              <h4>{isEditing ? form.regNo || "Vehicle" : "Add New Vehicle"}</h4>
            </div>
          </div>
        </div>

        {readOnly && (
          <div className="alert alert-info">
            You have read-only access. {isEditing ? "Editing" : "Creating"} vehicles requires
            the Vehicles.{isEditing ? "Edit" : "Create"} permission.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Branch</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="form-control"
                    value={form.branchCode || branches.find((b) => b.branchId === form.branchId)?.branchCode || ""}
                    readOnly
                    disabled
                  />
                ) : (
                  <select
                    className="form-control"
                    value={form.branchId}
                    onChange={setField("branchId")}
                    disabled={readOnly}
                  >
                    <option value="">--Select--</option>
                    {availableBranches.map((b) => (
                      <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Registration No</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.regNo}
                  onChange={setField("regNo")}
                  disabled={readOnly}
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Brand</label>
                <input type="text" className="form-control" value={form.brand || ""} onChange={setField("brand")} disabled={readOnly} />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Model</label>
                <input type="text" className="form-control" value={form.model || ""} onChange={setField("model")} disabled={readOnly} />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Make Year</label>
                <input type="number" className="form-control" value={form.makeYear ?? ""} onChange={setField("makeYear")} disabled={readOnly} />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Last Service Mileage</label>
                <input type="number" className="form-control" value={form.lastServiceMileage ?? ""} onChange={setField("lastServiceMileage")} disabled={readOnly} />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Next Service Due Mileage</label>
                <input type="number" className="form-control" value={form.nextServiceDueMileage ?? ""} onChange={setField("nextServiceDueMileage")} disabled={readOnly} />
              </div>
              <div className="col-md-3 mb-3 d-flex align-items-end">
                <ToggleSwitch
                  id="vehicle-active"
                  checked={form.isActive}
                  onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                  disabled={readOnly}
                  label={form.isActive ? "Active" : "Not In Use"}
                />
              </div>
            </div>

            <hr />

            <h5 className="mb-3">Allocation</h5>
            <div className="row">
              <div className="col-12 mb-3">
                <div className="d-flex gap-4">
                  <div className="form-check">
                    <input
                      type="radio"
                      className="form-check-input"
                      id="alloc-designation"
                      name="allocationMode"
                      checked={allocationMode === "designation"}
                      onChange={() => setAllocationMode("designation")}
                      disabled={readOnly}
                    />
                    <label className="form-check-label" htmlFor="alloc-designation">By Designation</label>
                  </div>
                  <div className="form-check">
                    <input
                      type="radio"
                      className="form-check-input"
                      id="alloc-employee"
                      name="allocationMode"
                      checked={allocationMode === "employee"}
                      onChange={() => setAllocationMode("employee")}
                      disabled={readOnly}
                    />
                    <label className="form-check-label" htmlFor="alloc-employee">By Employee</label>
                  </div>
                </div>
              </div>
              {allocationMode === "designation" ? (
                <div className="col-md-6 mb-3">
                  <label className="form-label">Designation</label>
                  <select
                    className="form-control"
                    value={designationCode}
                    onChange={(e) => setDesignationCode(e.target.value)}
                    disabled={readOnly}
                  >
                    <option value="">--Select--</option>
                    {designations.map((d) => (
                      <option key={d.designationCode} value={d.designationCode}>{d.designationName}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="col-md-6 mb-3">
                  <label className="form-label">Employee</label>
                  {resolvedEmployee ? (
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="form-control"
                        value={`${resolvedEmployee.empName} ${resolvedEmployee.empLastName || ""}`.trim()}
                        readOnly
                        disabled
                      />
                      {!readOnly && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary flex-shrink-0"
                          onClick={() => setResolvedEmployee(null)}
                        >
                          Change
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="d-flex gap-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Employee code"
                        value={empCodeInput}
                        onChange={(e) => setEmpCodeInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleFindEmployee();
                          }
                        }}
                        disabled={readOnly || isSearchingEmpCode}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-primary flex-shrink-0"
                        onClick={handleFindEmployee}
                        disabled={readOnly || isSearchingEmpCode || !empCodeInput.trim()}
                      >
                        {isSearchingEmpCode ? "Searching..." : "Find"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {!readOnly && (
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isCreating || isUpdating}>
                  Save
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => navigate(paths.vehicles)}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleForm;
